import React, {createContext, ReactElement, useEffect, useState} from 'react';
import {UserProfile} from "../models/userProfile/userProfile.model";
import {
    addIndexedData,
    checkIfDataExists,
    deleteAllIndexedData,
    deleteIndexedData,
    getAllIndexedData,
    initDB,
    Stores,
    updateIndexedData
} from "../index.db";
import {ThatsMeBabyContextType} from "../types/thatsMeBabyContext.type";
import {AuthToken} from "../types/authToken.type";
import {NavBarModeType} from "../types/navBarMode.type";
import {NavBarMode} from "../enums/navBarMode.enum";
import {CustomError} from "../models/customError/customError.model";
import {userProfileGetById} from "../../api/userProfileService";
import {ErrorHelper} from "../utils/error.helper";
import {AxiosError} from "axios";
import {tokenEventEmitter} from "../utils/eventEmitter.util";
import {toast} from "react-toastify";

const ThatsMeBabyContext = createContext<ThatsMeBabyContextType>(
    {
        profile: undefined,
        setProfile: async () => {},
        isLoggedIn: undefined,
        isFinishedLoadingProfileData: undefined,
        authToken: undefined,
        setAuthToken: async () => {},
        pageBackgroundColour: "",
        setPageBackgroundColour: () => null,
        navBar: {
            navTheme: NavBarMode.Light,
            setNavTheme: () => null,
            navActions: [],
            setNavActions: () => null
        },
        customError: undefined,
        setCustomError: async () => {},
        refreshCurrentUser: async () => {}
    }
);

interface ThatsMeBabyContextProps {
    children: React.ReactNode;
}
const ThatsMeBabyContextProvider = ({children} : ThatsMeBabyContextProps) =>
{
    const [currentUser, setCurrentUser] = useState<UserProfile>();
    const [authToken, setAuthToken] = useState<AuthToken>();
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>();
    const [navTheme, setNavTheme] = useState<NavBarModeType>(NavBarMode.Light);
    const [navActions, setNavActions] = useState<ReactElement[]>([]);
    const [isFinishedLoadingProfileData, setIsFinishedLoadingProfileData] = useState<boolean>();
    const [pageBackgroundColour, setPageBackgroundColour] = useState("transparent");
    const [isIndexDbInitialised, setIsIndexDbInitialised] = useState<boolean>(false);
    const [customError, setCustomError] = useState<CustomError>();

    useEffect(() => {
        let isMounted = true;
        const handleInitDB = async () => {
            await initDB();
            if(isMounted){
                await handleGetAuthToken();
                await handleGetCustomErrors();
                setIsIndexDbInitialised(true);
            }
        };

        void handleInitDB()

        return () => {
            isMounted = false; // Clean-up function to prevent state updates after unmount
        };
    }, []);

    useEffect(() => {
        const handleTokenRefresh = async (newToken: AuthToken) => {
            await handleSetAuthToken(newToken);
        };
        const handleTokenFailed = async () => {
            await handleSetAuthToken(undefined);
            toast.warning("You have been logged out. Please login again.");
        };

        tokenEventEmitter.on('tokenRefreshed', handleTokenRefresh);
        tokenEventEmitter.on('tokenFailed', handleTokenFailed);

        return () => {
            tokenEventEmitter.off('tokenRefreshed', handleTokenRefresh);
            tokenEventEmitter.off('tokenFailed', handleTokenFailed);
        };
    }, [setAuthToken]);

    useEffect(() => {
        void handleGetCurrentUser();
    }, [isLoggedIn, isIndexDbInitialised]);

    const handleGetCurrentUser = async () => {
        // Don't attempt to retrieve the user profile until the db has been initialised and the 
        // auth token has been attempted to be retrieved confirming whether user is logged in or not
        if(!isIndexDbInitialised)
        {
            return;
        }

        const userProfiles = await getAllIndexedData<UserProfile>(Stores.Users);
        let data = undefined;

        if(!Array.isArray(userProfiles) || userProfiles.length === 0) {
            if(authToken) {
                try {
                    const userFromClaims = await userProfileGetById();

                    if(userFromClaims) {
                        await handleSetUser(userFromClaims);
                        setIsFinishedLoadingProfileData(true);
                        return;
                    }
                }
                catch(error) {
                    // Failed to login but had a auth token, reset
                    await handleSetAuthToken(undefined);
                    
                    console.log(error);
                    ErrorHelper.showErrors(error as AxiosError);
                }
            }

            await handleSetUser(data);
            setIsFinishedLoadingProfileData(true);
            return;
        }

        if(isLoggedIn) {
            data = userProfiles.find(up => up.id !== -1);
        } else {
            data = userProfiles.find(up => up.id === -1);
        }

        await handleSetUser(data);
        setIsFinishedLoadingProfileData(true);
    };

    const handleGetAuthToken = async () => {
        const tokens = await getAllIndexedData<AuthToken>(Stores.Auth);
        if(Array.isArray(tokens) && tokens.length !== 0) {
            setAuthToken(tokens[0]);
            setIsLoggedIn(true);
            return;
        }

        setAuthToken(undefined);
        setIsLoggedIn(false);
    };

    const handleGetCustomErrors = async () => {
        const errors = await getAllIndexedData<CustomError>(Stores.Errors);
        setCustomError(Array.isArray(errors) && errors.length ? errors[0] : undefined);
    };

    const handleSetUser = async (profile: UserProfile | undefined) => {
        let recordExists = currentUser && await checkIfDataExists(Stores.Users, currentUser.id);
        if (typeof profile === 'undefined' && recordExists === true) {
            await deleteIndexedData(Stores.Users, currentUser!.id)
        }

        if(typeof profile !== 'undefined')
        {
            recordExists === true ? await updateIndexedData(Stores.Users, profile!.id, profile) : await addIndexedData(Stores.Users, profile)
        }

        setCurrentUser(profile);
    };
    
    const refreshCurrentUser = async () => {

        const userProfiles = await getAllIndexedData<UserProfile>(Stores.Users);

        // Cannot refresh data that does not exist
        if(!Array.isArray(userProfiles) || userProfiles.length === 0 || !authToken) {
            return;
        }

        try {
            const userFromClaims = await userProfileGetById();

            if(userFromClaims) {
                await handleSetUser(userFromClaims);
            }
        }
        catch(error) {
            // Failed to login but had a auth token, reset
            await handleSetAuthToken(undefined);

            console.error(error);
            ErrorHelper.showErrors(error as AxiosError);
        }
    };

    const handleSetError = async(error?: CustomError) => {

        const errors = await getAllIndexedData<CustomError>(Stores.Errors);

        if (typeof errors === 'string') {
            return;
        }

        for (let error of errors) {
            await deleteIndexedData(Stores.Errors, error.type)
        }

        if(error)
        {
            await addIndexedData(Stores.Errors, error);
        }

        setCustomError(error);
    };

    const handleSetAuthToken = async (auth: AuthToken | undefined) => {
        let recordExists = (auth && await checkIfDataExists(Stores.Auth, auth.id) as boolean);
        if (typeof auth === 'undefined') {
            await deleteAllIndexedData(Stores.Auth)
        }

        if(typeof auth !== 'undefined')
        {
            recordExists === true ? await updateIndexedData(Stores.Auth, auth!.id, auth) : await addIndexedData(Stores.Auth, auth)
        }

        setAuthToken(auth);

        if(!auth)
        {
            setIsLoggedIn(false);
            await handleSetUser(undefined);
            return;
        }

        setIsLoggedIn(true);
    };

    const handleSetNavTheme = (theme: NavBarModeType) => {
        setNavTheme(theme);
    };

    const handleSetNavActions = (actions: ReactElement[]) => {
        setNavActions(actions);
    };

    const handleSetPageBackgroundColour = (colour?: string) => {
        setPageBackgroundColour(String(colour));
    };

    return (
        <ThatsMeBabyContext.Provider
            value={{
                profile: currentUser,
                setProfile: handleSetUser,
                isLoggedIn: isLoggedIn,
                isFinishedLoadingProfileData: isFinishedLoadingProfileData,
                authToken: authToken,
                setAuthToken: handleSetAuthToken,
                pageBackgroundColour: pageBackgroundColour,
                setPageBackgroundColour: handleSetPageBackgroundColour,
                navBar: {
                    navTheme: navTheme,
                    setNavTheme: handleSetNavTheme,
                    navActions: navActions,
                    setNavActions: handleSetNavActions
                },
                customError,
                setCustomError: handleSetError,
                refreshCurrentUser
            }}
        >
            {children}
        </ThatsMeBabyContext.Provider>
    )
}

export {ThatsMeBabyContext, ThatsMeBabyContextProvider} ;